import {
  getAllIds,
  getCountByStatus,
  useGetAllArtworks,
} from "@/api/artwork/artwork";
import {
  useDeleteEnsembles,
  useRemoveArtworksFromEnsembles,
  useUpdateTemporarySet,
} from "@/api/ensemble/ensemble";
import ArtworkPrintDrawer from "@/components/artwork/ArtworkPrintDrawer";
import { NewArtworkJourney } from "@/components/artwork/NewArtworkJourney";
import DeleteSetModal from "@/components/ensemble/DeleteSetModal";
import CreateArtworkSet from "@/components/ensemble/LinkSetsAndArtworks";
import NewArtworkSetModal from "@/components/ensemble/NewArtworkSetModal";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import TableSelectionActions from "@/components/table/TableSelectionActions";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/datatable";
import {
  artworkColumnsFormatted,
  getArtworkAdvancedSearchConfig,
} from "@/config/dashboard/artwork";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import useArtworkSet from "@/hooks/useArtworkSet";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Artwork } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "artwork-table-preferences";

const flatVisibleArtworkColumns = artworkColumnsFormatted.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const ArtworkDashboard = () => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const artworkAdvancedSearchConfig =
    getArtworkAdvancedSearchConfig(instanceData);

  const navigate = useNavigate();
  const { mutateAsync: deleteEnsembles } = useDeleteEnsembles();

  const { artworkSet, isInArtworkSet } = useArtworkSet();

  const {
    fetchParams,
    filterStatus,
    setFilterStatus,
    pagination,
    setPagination,
    countByStatus,
    currentCount,
  } = useDashboardParams<Artwork>({
    resourceName: "artwork",
    resourceCounter: getCountByStatus,
    statusField: "oeuvresPublicationStatus",
  });

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleArtworkColumns;
    }
    return flatVisibleArtworkColumns;
  });

  const [editArtworkSetOpen, setEditArtworkSetOpen] = useState(false);
  const [deleteSetOpen, setDeleteSetOpen] = useState(false);
  const { mutateAsync: updateArtworkSet } = useUpdateTemporarySet();
  const { mutateAsync: removeArtworksFromEnsembles } =
    useRemoveArtworksFromEnsembles();

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...artworkColumnsFormatted.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...artworkColumnsFormatted.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  const { data } = useGetAllArtworks(fetchParams);

  const columns: ColumnDef<Artwork, any>[] = useMemo(() => {
    return generateTable(artworkColumnsFormatted, "artwork", t, instanceData);
  }, [t, instanceData]);

  const handleRowClick = (row: Row<Artwork>) => {
    navigate(`/edit/artwork/${row.original.id}`);
  };

  const handleDeleteSet = async (id: string) => {
    try {
      await deleteEnsembles(id);
      toast({
        title: t("ensembleDeleted"),
        variant: "success",
        description: t("ensembleDeletedDescription"),
      });
      navigate("/dashboard/artwork");
    } catch (error) {
      toast({
        title: t("error"),
        variant: "error",
        description: t("errorDescription"),
      });
      console.error(error);
    }
  };

  const pageTitle = artworkSet
    ? artworkSet.ensemblesDescriptionNomEnsemble
    : t("dashboardTitleArtworks");

  const [selectionState, setSelectionState] = useState({});

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{pageTitle}</p>
        <GlobalSearch config={artworkAdvancedSearchConfig} data={data ?? []} />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          {isInArtworkSet && (
            <Button
              variant="outline"
              className="flex flex-row items-center gap-2"
              onClick={() => setEditArtworkSetOpen(true)}
            >
              <Pencil size={16} />
              {t("updateEnsemble")}
            </Button>
          )}
          {isInArtworkSet && (
            <Button
              variant="outline"
              className="flex flex-row items-center gap-2"
              onClick={() => {
                if (artworkSet?.artworkCount === 0) {
                  handleDeleteSet(artworkSet.id);
                } else {
                  setDeleteSetOpen(true);
                }
              }}
            >
              <Trash2 size={16} />
            </Button>
          )}
          <AdvancedSearch
            config={artworkAdvancedSearchConfig}
            resource="artworks"
          />
          <AdvancedSort config={artworkAdvancedSearchConfig} />
          <NewArtworkJourney
            isArtmatchEnabled={!instanceData?.hideArtMatchBO}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={handleRowClick}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
          }}
          selection={{
            getRowId: (a) => a.id,
            getAllRowIds: () => getAllIds(fetchParams.where),
            state: {
              selectionState,
              setSelectionState,
            },
            enableSelectAll: true,
          }}
        />
        <TableSelectionActions
          selectedRows={selectionState}
          setSelectedRows={setSelectionState}
          selectActions={(selectedIds) => (
            <div className="flex flex-row gap-3 items-center">
              <ArtworkPrintDrawer selectedArtworksIds={selectedIds} />

              {!isInArtworkSet && (
                <CreateArtworkSet
                  selectedArtworksIds={selectedIds}
                  resetSelectedArtworks={() => setSelectionState({})}
                />
              )}
              {artworkSet && isInArtworkSet && (
                <Button
                  onClick={() =>
                    removeArtworksFromEnsembles({
                      ensembleIds: [artworkSet.id],
                      artworkIds: selectedIds,
                    })
                  }
                  variant="destructive"
                  className="flex flex-row gap-2 items-center"
                >
                  <Trash2 size={16} />
                  {t("removeArtworksFromSet")}
                </Button>
              )}
            </div>
          )}
        />

        {artworkSet && isInArtworkSet && (
          <NewArtworkSetModal
            open={editArtworkSetOpen}
            onOpenChange={setEditArtworkSetOpen}
            setToUpdate={artworkSet}
            onSubmit={(data) => {
              updateArtworkSet({ id: artworkSet.id, data });
              setEditArtworkSetOpen(false);
            }}
          />
        )}

        {artworkSet && (
          <DeleteSetModal
            open={deleteSetOpen}
            onOpenChange={setDeleteSetOpen}
            onSubmit={() => handleDeleteSet(artworkSet.id)}
          />
        )}
      </div>
    </div>
  );
};

export default ArtworkDashboard;
